import React, { useEffect, useRef, useState } from "react";
import FullscreenModal from "../common/FullscreenModal/FullscreenModal";
import { Checkbox } from "@mantine/core";
import * as d3 from "d3";
import "./billSummary.css";
import { useMantineColorScheme } from "@mantine/core";
import { FIELDS, COLORS, FIELD_LABELS, LEGEND_FIELDS, AFTER_BAR_COLORS } from "./billSummary.constants";
// After bar colors: first field green, rest grey
import { clamp } from "./billSummary.utils";
import { formatCompactNumber, formatDisplayNumber } from "../../utils/helpers";
import { projectDetailsAtom } from "../../utils/atoms/projectDetailsAtom";
import { useAtom } from "jotai";
import { useTranslation } from "react-i18next";

const EMPTY_ARRAY = [];

const BillSummaryChart = ({ beforeMG, afterMG, inModalInstance = false }) => {
  // Line graph visibility state
  const [showBeforeDemand, setShowBeforeDemand] = useState(true);
  const [showAfterDemand, setShowAfterDemand] = useState(true);
  const [showBeforeEnergy, setShowBeforeEnergy] = useState(true);
  const [showAfterEnergy, setShowAfterEnergy] = useState(true);
  const [fullscreenModal, setFullscreenModal] = useState(false);
  const [projectDetails] = useAtom(projectDetailsAtom);
  const chartContentRef = useRef();
  const ref = useRef();
  const svgRef = useRef();
  const { colorScheme } = useMantineColorScheme();
  const legendTextColor = colorScheme === "dark" ? "#e0e0e0" : "#222";
  const [containerWidth, setContainerWidth] = useState(800);
  const currencyCode = (projectDetails && projectDetails.projectDTO && projectDetails.projectDTO.currencyCode) ? projectDetails.projectDTO.currencyCode : "$";
  const { t } = useTranslation();

  // Zoom state: normalized [start, end] fraction of visible months
  const [zoomRange, setZoomRange] = useState([0, 1]);

  // Always use safe arrays for calculations
  const safeBeforeMG = Array.isArray(beforeMG) ? beforeMG : EMPTY_ARRAY;
  const safeAfterMG = Array.isArray(afterMG) ? afterMG : EMPTY_ARRAY;
  const hasBefore = safeBeforeMG.length > 0;
  const hasAfter = safeAfterMG.length > 0;
  const primarySeries = hasBefore ? safeBeforeMG : safeAfterMG;

  const allowedLegendKeys = React.useMemo(() => {
    const keys = [];
    if (hasBefore) {
      keys.push(...FIELDS.map((field) => `before-${field}`));
    }
    if (hasAfter) {
      keys.push(...FIELDS.map((field) => `after-${field}`));
    }
    return keys;
  }, [hasBefore, hasAfter]);

  // Compute which fields are non-zero in both beforeMG and afterMG
  const nonZeroFields = React.useMemo(() => {
    if (!hasBefore && !hasAfter) return FIELDS;
    return FIELDS.filter((field) => {
      const allBeforeZero =
        !hasBefore ||
        safeBeforeMG.every((bm) => !bm[field] || bm[field] === 0);
      const allAfterZero =
        !hasAfter ||
        safeAfterMG.every((am) => !am[field] || am[field] === 0);
      return !(allBeforeZero && allAfterZero);
    });
  }, [safeBeforeMG, safeAfterMG, hasBefore, hasAfter]);

  // Legend check/uncheck state
  // visibleFields now only tracks checked fields, but all available fields are always shown in the legend
  const [visibleFields, setVisibleFields] = useState(() => {
    return LEGEND_FIELDS.map((f) => f.key).filter((key) => {
      const [type, field] = key.split("-");
      return nonZeroFields.includes(field) && allowedLegendKeys.includes(key);
    });
  });

  // Remove fields from visibleFields only if they become unavailable, but always show all available fields in legend
  React.useEffect(() => {
    setVisibleFields((prev) => {
      const allowed = LEGEND_FIELDS.map((f) => f.key).filter((key) => {
        const [type, field] = key.split("-");
        return nonZeroFields.includes(field) && allowedLegendKeys.includes(key);
      });
      if (
        Array.isArray(prev) &&
        prev.length === allowed.length &&
        prev.every((value, index) => value === allowed[index])
      ) {
        return prev;
      }
      return allowed;
    });
  }, [nonZeroFields, allowedLegendKeys]);

  // Prepare chart data outside useEffect so it's available everywhere
  const data =
    hasBefore || hasAfter
      ? primarySeries.map((entry, i) => ({
          month: entry?.periodStart?.split("/")?.[1] || `M${i + 1}`,
          before: FIELDS.map((f) => (hasBefore ? safeBeforeMG?.[i]?.[f] ?? 0 : 0)),
          after: FIELDS.map((f) => (hasAfter ? safeAfterMG?.[i]?.[f] ?? 0 : 0)),
        }))
      : [];

  const getVisibleStackSum = (monthData, key, valueFilter = () => true) => {
    if (!monthData || !Array.isArray(monthData[key])) return 0;
    return FIELDS.reduce((sum, field, i) => {
      const legendKey = `${key}-${field}`;
      if (!visibleFields.includes(legendKey)) return sum;
      const value = monthData[key][i];
      if (!Number.isFinite(value) || !valueFilter(value)) return sum;
      return sum + value;
    }, 0);
  };

  // Tooltip state
  const [tooltip, setTooltip] = useState({
    visible: false,
    x: 0,
    y: 0,
    value: "",
    field: "",
    month: "",
  });
  const [isBarHovering, setIsBarHovering] = useState(false);
  const tooltipTimeout = useRef();

  useEffect(() => {
    // Hide tooltip on any scroll event
    function handleHideTooltipOnScroll() {
      const tooltipDiv = document.getElementById("bill-summary-tooltip");
      if (tooltipDiv) tooltipDiv.style.display = "none";
    }
    window.addEventListener("scroll", handleHideTooltipOnScroll, true);

    // Resize handler
    function handleResize() {
      if (ref.current && ref.current.parentElement) {
        setContainerWidth(ref.current.parentElement.offsetWidth);
      }
    }
    window.addEventListener("resize", handleResize);
    // Initial size
    handleResize();

    // Clean up listeners
    return () => {
      window.removeEventListener("scroll", handleHideTooltipOnScroll, true);
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    return () => {
      const tooltipDiv = document.getElementById("bill-summary-tooltip");
      if (tooltipDiv) tooltipDiv.remove();
    };
  }, []);

  useEffect(() => {
    // Safety: check ref.current and data validity
    if (!ref.current) {
      console.warn("Chart container ref not set, skipping chart render.");
      return;
    }
    if (!hasBefore && !hasAfter) {
      console.warn("Chart data missing or empty, skipping chart render.");
      return;
    }
    // Check for NaN/undefined in data
    const hasInvalid =
      (hasBefore &&
        safeBeforeMG.some((bm) => FIELDS.some((f) => bm[f] == null || isNaN(bm[f])))) ||
      (hasAfter &&
        safeAfterMG.some((am) => FIELDS.some((f) => am[f] == null || isNaN(am[f]))));
    if (hasInvalid) {
      console.error("Chart data contains NaN or undefined values. Check input data.");
      return;
    }
    // ...existing code...
    // When rendering line graphs, check visibility state
    // Example for D3 line rendering:
    // if (showBeforeDemand) { ...render before demand line... }
    // if (showAfterDemand) { ...render after demand line... }
    // if (showBeforeEnergy) { ...render before energy line... }
    // if (showAfterEnergy) { ...render after energy line... }
  }, [
    beforeMG,
    afterMG,
    hasBefore,
    hasAfter,
    safeBeforeMG,
    safeAfterMG,
    colorScheme,
    zoomRange,
    containerWidth,
    visibleFields,
    showBeforeDemand,
    showAfterDemand,
    showBeforeEnergy,
    showAfterEnergy,
  ]);

  // Zoom button overlay (like stackedBarChart)
  let margin = { top: 15, right: 60, bottom: 40, left: 60 }; // reduced margins to maximize plot area
  // Increase base chart height to make better use of available vertical space
  let baseHeight = 550 - margin.top - margin.bottom;
  let height = baseHeight;
  const width = containerWidth - margin.left - margin.right;
  // --- Precompute lowest negative bar for each tick to adjust chart height ---
  let maxLabelY = baseHeight + 16; // default label position
  if (hasBefore || hasAfter) {
    const data = primarySeries.map((entry, i) => ({
      month: entry?.periodStart?.split("/")?.[1] || `M${i + 1}`,
      before: FIELDS.map((f) => (hasBefore ? safeBeforeMG?.[i]?.[f] ?? 0 : 0)),
      after: FIELDS.map((f) => (hasAfter ? safeAfterMG?.[i]?.[f] ?? 0 : 0)),
    }));
    let [zs, ze] = zoomRange;
    zs = Math.max(0, Math.min(1, zs));
    ze = Math.max(zs, Math.min(1, ze));
    let startIdx = Math.floor(zs * data.length);
    let endIdx = Math.ceil(ze * data.length);
    if (endIdx <= startIdx) endIdx = Math.min(data.length, startIdx + 1);
    const visibleData = data.slice(startIdx, endIdx);
    const maxY = visibleData.length > 0
      ? d3.max(visibleData, (d) =>
          Math.max(
            getVisibleStackSum(d, "before"),
            getVisibleStackSum(d, "after")
          )
        )
      : 100;
    const y = d3.scaleLinear()
      .domain([0, maxY * 1.1])
      .range([baseHeight, 0]);
    visibleData.forEach((d) => {
      let lowestY = baseHeight; // default: bottom of chart
      ["before", "after"].forEach((key) => {
        if (d[key]) {
          let yNeg = 0;
          FIELDS.forEach((field, i) => {
            const legendKey = `${key}-${field}`;
            if (!visibleFields.includes(legendKey)) return;
            const val = d[key][i];
            if (val < 0) {
              const yStart = y(yNeg);
              const yEnd = y(yNeg + val);
              const barY = Math.max(yStart, yEnd); // lower pixel value
              if (barY > lowestY) lowestY = barY;
              yNeg += val;
            }
          });
        }
      });
      if (lowestY + 18 > maxLabelY) maxLabelY = lowestY + 18;
    });
    // If label would be below chart, increase height and margin.bottom
    if (maxLabelY > baseHeight + 16) {
      height = maxLabelY;
      margin = { ...margin, bottom: maxLabelY - baseHeight + 32 };
    }
  }

  // Prevent the chart from growing beyond a reasonable maximum height to avoid multi-page charts
  const MAX_CHART_HEIGHT = 680;
  if (height > MAX_CHART_HEIGHT) {
    height = MAX_CHART_HEIGHT;
    // Keep bottom margin reasonable when clamping
    margin = { ...margin, bottom: Math.min(margin.bottom || 40, 60) };
  }

  // Determine zoom indices
  const totalMonths = data.length;
  let [zs, ze] = zoomRange;
  zs = Math.max(0, Math.min(1, zs));
  ze = Math.max(zs, Math.min(1, ze));
  let startIdx = Math.floor(zs * totalMonths);
  let endIdx = Math.ceil(ze * totalMonths);
  if (endIdx <= startIdx) endIdx = Math.min(totalMonths, startIdx + 1);
  const visibleData = data.slice(startIdx, endIdx);

  d3.select(ref.current).selectAll("svg").remove();

  const svgEl = d3
    .select(ref.current)
    .append("svg")
    .attr("width", containerWidth)
    .attr("height", height + margin.top + margin.bottom)
    .on("mouseleave", () => {
      setTooltip({ visible: false });
    })
    .node();
  svgRef.current = svgEl;
  const svg = d3.select(svgEl);
  // Axes group
  const axesG = svg
    .append("g")
    .attr("class", "axes-area")
    .attr("transform", `translate(${margin.left},${margin.top})`);
  // Plot area group (bars)
  const plotG = svg
    .append("g")
    .attr("class", "plot-area")
    .attr("transform", `translate(${margin.left},${margin.top})`);
  // Demand line group (above bars)
  const demandLineG = svg
    .append("g")
    .attr("class", "demand-line-area")
    .attr("transform", `translate(${margin.left},${margin.top})`);
  // Demand hover group (topmost)
  const demandHoverG = svg
    .append("g")
    .attr("class", "demand-hover-area")
    .attr("transform", `translate(${margin.left},${margin.top})`);

  // X scale for zoomed domain
  const xDomain =
    visibleData.length > 0
      ? visibleData.map((d) => d.month)
      : [
          "Jan",
          "Feb",
          "Mar",
          "Apr",
          "May",
          "Jun",
          "Jul",
          "Aug",
          "Sep",
          "Oct",
          "Nov",
          "Dec",
        ];
  const x0 = d3.scaleBand().domain(xDomain).range([0, width]).padding(0.12);
  const x1 = d3
    .scaleBand()
    .domain(["before", "after"])
    .range([0, x0.bandwidth()])
    .padding(0.02);
  // Y scale - calculate bar chart data bounds
  const maxY =
    visibleData.length > 0
      ? d3.max(visibleData, (d) =>
          Math.max(
            getVisibleStackSum(d, "before"),
            getVisibleStackSum(d, "after")
          )
        )
      : 100;
  
  // Calculate min for bar chart (considering negative stacked values)
  const minY =
    visibleData.length > 0
      ? d3.min(visibleData, (d) =>
          Math.min(
            getVisibleStackSum(d, "before", (value) => value < 0),
            getVisibleStackSum(d, "after", (value) => value < 0)
          )
        )
      : 0;

  // Right-side y-axes for demand and energy line charts
  const beforeDemand =
    hasBefore
      ? safeBeforeMG.slice(startIdx, endIdx).map((bm) => bm.demand)
      : [];
  const afterDemand =
    hasAfter
      ? safeAfterMG.slice(startIdx, endIdx).map((am) => am.demand)
      : [];
  const beforeEnergy =
    hasBefore
      ? safeBeforeMG.slice(startIdx, endIdx).map((bm) => bm.energy)
      : [];
  const afterEnergy =
    hasAfter
      ? safeAfterMG.slice(startIdx, endIdx).map((am) => am.energy)
      : [];
  // Only include visible lines in max/min calculation
  let visibleLineValues = [];
  if (showBeforeDemand) visibleLineValues = visibleLineValues.concat(beforeDemand);
  if (showAfterDemand) visibleLineValues = visibleLineValues.concat(afterDemand);
  if (showBeforeEnergy) visibleLineValues = visibleLineValues.concat(beforeEnergy);
  if (showAfterEnergy) visibleLineValues = visibleLineValues.concat(afterEnergy);
  const maxDemandEnergy = visibleLineValues.length > 0 ? Math.max(...visibleLineValues) : 100;
  const minDemandEnergy = visibleLineValues.length > 0 ? Math.min(...visibleLineValues) : 0;
  
  // Add 10% padding to data ranges
  const barPaddedMin = minY < 0 ? minY * 1.1 : 0;
  // Ensure the padded max includes zero so the zero line is always inside the domain
  const barPaddedMax = Math.max(maxY * 1.1, 0);
  const demandPaddedMin = minDemandEnergy < 0 ? minDemandEnergy * 1.1 : 0;
  const demandPaddedMax = Math.max(maxDemandEnergy * 1.1, 0);
  
  // Calculate zero position ratio for each scale to align them
  const barZeroRatio = (barPaddedMax - barPaddedMin) > 0 ? -barPaddedMin / (barPaddedMax - barPaddedMin) : 0;
  const demandZeroRatio = (demandPaddedMax - demandPaddedMin) > 0 ? -demandPaddedMin / (demandPaddedMax - demandPaddedMin) : 0;
  
  // Use the larger ratio to ensure both datasets fit and 0 aligns
  const alignedZeroRatio = Math.max(barZeroRatio, demandZeroRatio);
  
  // Adjust domains to align 0 at the same pixel position
  let finalBarMin = barPaddedMin;
  let finalBarMax = barPaddedMax;
  let finalDemandMin = demandPaddedMin;
  let finalDemandMax = demandPaddedMax;
  
  if (alignedZeroRatio > 0 && alignedZeroRatio < 1) {
    const barNegRange = Math.abs(barPaddedMin);
    const barPosRange = barPaddedMax;
    const demandNegRange = Math.abs(demandPaddedMin);
    const demandPosRange = demandPaddedMax;
    
    // Adjust domains to maintain aligned zero ratio
    if (barNegRange / (barNegRange + barPosRange) < alignedZeroRatio) {
      finalBarMin = -barPosRange * alignedZeroRatio / (1 - alignedZeroRatio);
    } else {
      finalBarMax = barNegRange * (1 - alignedZeroRatio) / alignedZeroRatio;
    }
    
    if (demandNegRange / (demandNegRange + demandPosRange) < alignedZeroRatio) {
      finalDemandMin = -demandPosRange * alignedZeroRatio / (1 - alignedZeroRatio);
    } else {
      finalDemandMax = demandNegRange * (1 - alignedZeroRatio) / alignedZeroRatio;
    }
  }
  
  const y = d3
    .scaleLinear()
    .domain([finalBarMin, finalBarMax])
    .range([height, 0]);

  // Always draw axes
  axesG
    .append("g")
  .attr("transform", `translate(0,${height})`)
  .call(d3.axisBottom(x0));
  axesG.append("g").call(d3.axisLeft(y).tickFormat(formatCompactNumber));

  // Separate scale for demand/energy lines with aligned 0 at same pixel position
  const yDemand = d3
    .scaleLinear()
    .domain([finalDemandMin, finalDemandMax])
    .range([height, 0]);
  axesG
    .append("g")
    .attr("transform", `translate(${width},0)`)
    .call(d3.axisRight(yDemand).tickFormat(formatCompactNumber));

  // Line generator for right y-axis
  const lineGen = d3
    .line()
    .x((d, i) => x0(xDomain[i]) + x0.bandwidth() / 2)
    .y((d) => yDemand(d));

  // Plot beforeMG demand line (dark green)
  if (showBeforeDemand) {
    demandLineG
      .append("path")
      .datum(beforeDemand)
      .attr("fill", "none")
      .attr("stroke", "#1b5e20") // dark green
      .attr("stroke-width", 2)
      .attr("d", lineGen);
  }
  // Plot afterMG demand line (light green)
  if (showAfterDemand) {
    demandLineG
      .append("path")
      .datum(afterDemand)
      .attr("fill", "none")
      .attr("stroke", "#81c784") // light green
      .attr("stroke-width", 2)
      .attr("d", lineGen);
  }
  // Plot beforeMG energy line (dark orange)
  if (showBeforeEnergy) {
    demandLineG
      .append("path")
      .datum(beforeEnergy)
      .attr("fill", "none")
      .attr("stroke", "#e65100") // dark orange
      .attr("stroke-width", 2)
      .attr("d", lineGen);
  }
  // Plot afterMG energy line (light orange)
  if (showAfterEnergy) {
    demandLineG
      .append("path")
      .datum(afterEnergy)
      .attr("fill", "none")
      .attr("stroke", "#ffd54f") // light orange
      .attr("stroke-width", 2)
      .attr("d", lineGen);
  }

  // Show vertical line only when hovering over chart area, at nearest month
  // Draw vertical line at nearest month to mouse position
  svg.on("mousemove", function (event) {
    // Only show line tooltip if not hovering a bar
    if (isBarHovering) {
      let tooltipDiv = document.getElementById("bill-summary-tooltip");
      if (tooltipDiv) tooltipDiv.style.display = "none";
      return;
    }
    let tooltipDiv = document.getElementById("bill-summary-tooltip");
    if (tooltipDiv) tooltipDiv.style.display = "block";
    const [mx, my] = d3.pointer(event, this);
    // Remove previous vertical line and dots
    demandHoverG.selectAll(".sync-vertical-line").remove();
    demandHoverG.selectAll(".sync-highlight-point").remove();
    // Find nearest month index
    const chartX = mx - margin.left;
    let nearestIdx = 0;
    let minDist = Infinity;
    xDomain.forEach((month, i) => {
      const cx = x0(month) + x0.bandwidth() / 2;
      const dist = Math.abs(chartX - cx);
      if (dist < minDist) {
        minDist = dist;
        nearestIdx = i;
      }
    });
    // Only show if mouse is inside plot area
    if (chartX >= 0 && chartX <= width) {
      const cx = x0(xDomain[nearestIdx]) + x0.bandwidth() / 2;
      const beforeDemandVal = beforeDemand[nearestIdx];
      const afterDemandVal = afterDemand[nearestIdx];
      const beforeEnergyVal = beforeEnergy[nearestIdx];
      const afterEnergyVal = afterEnergy[nearestIdx];
      const showBeforeDemandPoint =
        hasBefore && showBeforeDemand && Number.isFinite(beforeDemandVal);
      const showAfterDemandPoint =
        hasAfter && showAfterDemand && Number.isFinite(afterDemandVal);
      const showBeforeEnergyPoint =
        hasBefore && showBeforeEnergy && Number.isFinite(beforeEnergyVal);
      const showAfterEnergyPoint =
        hasAfter && showAfterEnergy && Number.isFinite(afterEnergyVal);
      demandHoverG
        .append("line")
        .attr("class", "sync-vertical-line")
        .attr("x1", cx)
        .attr("x2", cx)
        .attr("y1", 0)
        .attr("y2", height)
        .attr("stroke", "#bbb")
        .attr("stroke-width", 1.5)
        .attr("opacity", 0.7);
      // Add highlight dots for all 4 lines
      const dotData = [
        showBeforeDemandPoint ? { val: beforeDemandVal, color: "#1b5e20" } : null,
        showAfterDemandPoint ? { val: afterDemandVal, color: "#81c784" } : null,
        showBeforeEnergyPoint ? { val: beforeEnergyVal, color: "#e65100" } : null,
        showAfterEnergyPoint ? { val: afterEnergyVal, color: "#ffd54f" } : null,
      ].filter(Boolean);
      dotData.forEach((d) => {
        demandHoverG
          .append("circle")
          .attr("class", "sync-highlight-point")
          .attr("cx", cx)
          .attr("cy", yDemand(d.val))
          .attr("r", 8)
          .attr("fill", d.color)
          .attr("stroke", "#222")
          .attr("stroke-width", 2)
          .attr("opacity", 0.95);
      });
      // Show tooltip using direct DOM manipulation, always at vertical line
      let tooltipDiv = document.getElementById("bill-summary-tooltip");
      if (!tooltipDiv) {
        tooltipDiv = document.createElement("div");
        tooltipDiv.id = "bill-summary-tooltip";
        tooltipDiv.className = "bill-summary-tooltip" + (colorScheme === "dark" ? " bill-summary-tooltip-dark" : "");
        document.body.appendChild(tooltipDiv);
      }
      let left, top;
      const tooltipWidth = 200;
      if (inModalInstance && chartContentRef.current) {
        // Tooltip inside chart container, position absolute
        tooltipDiv.style.position = "absolute";
        left = cx - 15 * (nearestIdx + 1);
        top = margin.top + 8;
        // Clamp tooltip to chart area, reverse direction if overflowing right edge
        const chartRight = width;
        if (left + tooltipWidth > chartRight - 8) {
          left = cx - tooltipWidth - 16;
        }
        if (left < 8) {
          left = 8;
        }
      } else {
        // Tooltip in document body, position fixed
        const svgRect = svgEl.getBoundingClientRect();
        const chartLeft = svgRect.left + margin.left;
        const chartTop = svgRect.top + margin.top;
        left = chartLeft + cx - 15 * (nearestIdx + 1);
        top = chartTop + 8;
        const chartRight = chartLeft + width;
        if (left + tooltipWidth > chartRight - 8) {
          left = chartLeft + cx - tooltipWidth - 16;
        }
        if (left < chartLeft + 8) {
          left = chartLeft + 8;
        }
      }
      tooltipDiv.style.left = left + "px";
      tooltipDiv.style.top = top + "px";
      const tooltipRows = [];
      if (showBeforeDemandPoint) {
        tooltipRows.push(
          '<div>' +
            t('result.billSummary.tooltip.beforeDemand') +
            ' <span class="bill-summary-tooltip-before-demand">' +
            formatDisplayNumber(beforeDemandVal) +
            '</span></div>'
        );
      }
      if (showAfterDemandPoint) {
        tooltipRows.push(
          '<div>' +
            t('result.billSummary.tooltip.afterDemand') +
            ' <span class="bill-summary-tooltip-after-demand">' +
            formatDisplayNumber(afterDemandVal) +
            '</span></div>'
        );
      }
      if (showBeforeEnergyPoint) {
        tooltipRows.push(
          '<div>' +
            t('result.billSummary.tooltip.beforeEnergy') +
            ' <span class="bill-summary-tooltip-before-energy">' +
            formatDisplayNumber(beforeEnergyVal) +
            '</span></div>'
        );
      }
      if (showAfterEnergyPoint) {
        tooltipRows.push(
          '<div>' +
            t('result.billSummary.tooltip.afterEnergy') +
            ' <span class="bill-summary-tooltip-after-energy">' +
            formatDisplayNumber(afterEnergyVal) +
            '</span></div>'
        );
      }
      tooltipDiv.innerHTML =
        '<div class="bill-summary-tooltip-title">' + xDomain[nearestIdx] + '</div>' +
        tooltipRows.join('');
    }
  });
  // Remove vertical line and tooltip on mouseleave
  svg.on("mouseleave", function () {
    setTooltip({ visible: false, type: null });
    setIsBarHovering(false);
    const tooltipDiv = document.getElementById("bill-summary-tooltip");
    if (tooltipDiv) tooltipDiv.remove();
  });

  // Hide bar tooltip if mouse moves outside chart area (global mousemove)
  useEffect(() => {
    const handleDocumentMouseMove = (e) => {
      if (!ref.current) return;
      const bounds = ref.current.getBoundingClientRect();
      if (
        e.clientX < bounds.left ||
        e.clientX > bounds.right ||
        e.clientY < bounds.top ||
        e.clientY > bounds.bottom
      ) {
        setTooltip({ visible: false, type: null });
        setIsBarHovering(false);
      }
    };

    document.addEventListener("mousemove", handleDocumentMouseMove);
    return () => {
      document.removeEventListener("mousemove", handleDocumentMouseMove);
    };
  }, []);

  // Add axis label for right y-axis
  svg
  .append("text")
  .attr("transform", `rotate(-90)`)
  .attr("x", -height / 2)
  .attr("y", width + margin.left + 80)
  .attr("text-anchor", "middle")
  .style("font-size", "15px")
  .style("font-family", "Nunito, sans-serif")
  .style("fill", legendTextColor)
  .text(t("result.billSummary.axis.rightLabel")); // right y-axis label

  // Draw stacked bars (only for checked fields)
  if (visibleData.length > 0) {
    const stackKeys = [
      ...(hasBefore ? ["before"] : []),
      ...(hasAfter ? ["after"] : []),
    ];
    stackKeys.forEach((key, groupIdx) => {
      plotG
        .selectAll(`.bar-${key}`)
        .data(visibleData)
        .enter()
        .append("g")
        .attr("class", `bar-${key}`)
        .attr("transform", (d) => `translate(${x0(d.month) + x1(key)},0)`)
        .each(function (d) {
          let y0Pos = 0;
          let y0Neg = 0;
          if (
            Array.isArray(FIELDS) &&
            Array.isArray(visibleFields) &&
            d &&
            d[key]
          ) {
            FIELDS.forEach((field, i) => {
              const legendKey = `${key}-${field}`;
              if (!visibleFields.includes(legendKey)) return;
              if (!Array.isArray(d[key])) return;
              const val = d[key][i];
              const barLeft = 0;
              const barRight = x1.bandwidth();
              let yStart, yEnd, barY, barHeight;
              if (val >= 0) {
                yStart = y(y0Pos);
                yEnd = y(y0Pos + val);
                barY = Math.min(yStart, yEnd);
                barHeight = Math.abs(yEnd - yStart);
              } else {
                yStart = y(y0Neg);
                yEnd = y(y0Neg + val);
                barY = Math.min(yStart, yEnd);
                barHeight = Math.abs(yEnd - yStart);
              }
              // Guard: skip if barHeight is not positive or barY is NaN
              if (!isFinite(barHeight) || barHeight <= 0 || !isFinite(barY))
                return;
              // Set bar color: before = blue, after = green/grey
              let barColor;
              if (key === "before") {
                barColor = "#228be6"; // blue
              } else if (key === "after") {
                barColor = AFTER_BAR_COLORS[i] || "#bbb";
              } else {
                barColor = COLORS[i];
              }
              d3.select(this)
                .append("rect")
                .attr("x", barLeft)
                .attr("y", barY)
                .attr("width", barRight - barLeft)
                .attr("height", barHeight)
                .attr("fill", barColor)
                .on("mouseover", (event) => {
                  // setIsBarHovering(true);
                  const lineTooltip = document.getElementById(
                    "bill-summary-tooltip"
                  );
                  if (lineTooltip) lineTooltip.style.display = "none";
                  setTooltip({
                    visible: true,
                    x: event.clientX + 32,
                    y: event.clientY - 24,
                    value: val,
                    field: `${key} ${field}`,
                    month: d.month,
                    type: "bar",
                  });
                })
                .on("mousemove", (event) => {
                  setTooltip((t) => ({
                    ...t,
                    x: event.clientX + 32,
                    y: event.clientY - 24,
                  }));
                })
                .on("mouseout", () => {
                  setIsBarHovering(false);
                  setTooltip({ visible: false, type: null });
                });
              // Add reference lines for afterMG only
              if (key === "after" && val !== 0) {
                const barTop = val >= 0 ? y(y0Pos + val) : y(y0Neg + val);
                const barBottom = val >= 0 ? y(y0Pos) : y(y0Neg);
                const barHeightRef = Math.abs(barBottom - barTop);
                const barWidth = barRight - barLeft;
                const numLines = Math.max(3, Math.floor(barHeightRef / 15));
                const angleRad = (40 * Math.PI) / 180;
                const tanAngle = Math.tan(angleRad);
                for (let l = 1; l <= numLines; l++) {
                  const minY = Math.min(barTop, barBottom);
                  const maxY = Math.max(barTop, barBottom);
                  const yStartRef =
                    barTop + ((barBottom - barTop) * l) / (numLines + 1);
                  let maxLen = barWidth;
                  if (yStartRef - maxLen * tanAngle < minY) {
                    maxLen = (yStartRef - minY) / tanAngle;
                  }
                  if (yStartRef - maxLen * tanAngle > maxY) {
                    maxLen = (yStartRef - maxY) / tanAngle;
                  }
                  maxLen = Math.max(0, Math.min(barWidth, maxLen));
                  const x2 = barLeft + maxLen;
                  const yEndRef = yStartRef - maxLen * tanAngle;
                  d3.select(this)
                    .append("line")
                    .attr("x1", barLeft)
                    .attr("y1", yStartRef)
                    .attr("x2", x2)
                    .attr("y2", yEndRef)
                    .attr("stroke", "#222")
                    .attr("stroke-width", 1.2)
                    .attr("opacity", 0.18);
                }
              }
              // Update stacking baselines
              if (val >= 0) {
                y0Pos += val;
              } else {
                y0Neg += val;
              }
            });
          }
        });
    });
  }
    // --- Move x-axis labels below lowest negative bar for each tick ---
    // Find lowest negative bar for each tick
    if (visibleData.length > 0) {
      const lowestNegYByMonth = {};
      let highestNegBarLength = 0;
      visibleData.forEach((d) => {
        let lowestY = height; // default: bottom of chart
        ["before", "after"].forEach((key) => {
          if (d[key]) {
            let yNeg = 0;
            FIELDS.forEach((field, i) => {
              const legendKey = `${key}-${field}`;
              if (!visibleFields.includes(legendKey)) return;
              const val = d[key][i];
              if (val < 0) {
                const yStart = y(yNeg);
                const yEnd = y(yNeg + val);
                const barY = Math.max(yStart, yEnd); // lower pixel value
                if (barY > lowestY) lowestY = barY;
                const negBarLength = Math.abs(yEnd - yStart);
                if (negBarLength > highestNegBarLength) highestNegBarLength = negBarLength;
                yNeg += val;
              }
            });
          }
        });
        lowestNegYByMonth[d.month] = lowestY;
      });
      // Select x-axis labels and move them
      axesG.select("g") // axisBottom group
        .selectAll(".tick")
        .each(function (d) {
          const label = d3.select(this).select("text");
          const month = d;
          let newY = height + 10; // default offset below axis
          if (lowestNegYByMonth[month] > height) {
            // No negative bar, use default
            // Dynamically set offset based on highest negative bar's length across all months
            newY = height + Math.max(highestNegBarLength + 4, 32);
          } else {
            // Move label just below lowest negative bar
            newY = lowestNegYByMonth[month] + 18; // 18px below bar
          }
          label.attr("y", newY - height); // axisBottom labels are relative to axis line
        });
    }

  // Mantine dark/light mode and Nunito font
  // Add legend in right 20%
  svg
    .append("text")
    .attr("x", width / 2)
    .attr("y", height + margin.bottom + 12)
    .attr("text-anchor", "middle")
    .style("font-family", "Nunito, sans-serif")
    .style("fill", legendTextColor)
    .text(t("result.billSummary.axis.month"));
  // Get currency code for y-axis label from projectDetails atom
  svg
    .append("text")
    .attr("transform", "rotate(-90)")
    .attr("x", -height / 2)
    .attr("y", 20)
    .attr("text-anchor", "middle")
    .style("font-size", "15px")
    .style("font-family", "Nunito, sans-serif")
    .style("fill", legendTextColor)
    .text(`Cost (${currencyCode})`); // left y-axis label
  // Remove D3 legend, will use React legend below

  // Zoom button overlay (like stackedBarChart)
  const zoomBtnStyle = {
    position: "absolute",
    left: 24,
    right: "auto",
    top: 8,
    display: "flex",
    gap: "8px",
    zIndex: 10,
  };

  return (
    <>
      <div
        ref={chartContentRef}
        className="bill-summary-chart-container"
        style={inModalInstance ? { width: "100%", height: "100%" } : {}}
      >
        {/* Chart zoom wrapper */}
        <div style={{ width: "100%" }}>
          <div style={{ transformOrigin: "top left", width: "100%", minWidth: 0 }}>
            {/* Compact Legends: Before and After in single rows, smaller font, toolbar at end */}
            <div style={{ width: "100%", display: "flex", flexDirection: "row", alignItems: "flex-start", gap: 4, padding: "8px 0 4px 0" }}>
              <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 4 }}>
                {/* Before row */}
                <div className="bill-summary-legend-row">
                  {hasBefore && (
                    <>
                  <span className="bill-summary-legend-label" style={{ color: colorScheme === "dark" ? "#e0e0e0" : "#222" }}>{t("result.billSummary.legend.before")}</span>
                  {FIELDS.map((field, i) => {
                    const legendKey = `before-${field}`;
                    // Only show if field is available (nonZeroFields)
                    if (!nonZeroFields.includes(field)) return null;
                    // Use blue for before, AFTER_BAR_COLORS for after
                    const legendColor = "#228be6";
                    return (
                      <span key={legendKey} className="bill-summary-legend-item">
                        <Checkbox
                          checked={visibleFields.includes(legendKey)}
                          onChange={(e) => {
                            setVisibleFields((v) =>
                              e.target.checked
                                ? [...v, legendKey]
                                : v.filter((f) => f !== legendKey)
                            );
                          }}
                          size="xs"
                          radius="sm"
                          color="gray"
                          className="bill-summary-legend-checkbox"
                          aria-label={FIELD_LABELS[field]}
                        />
                        <span className="bill-summary-legend-color" style={{ background: legendColor }}></span>
                        <span className="bill-summary-legend-label" style={{ color: colorScheme === "dark" ? "#e0e0e0" : "#222", fontWeight: 600 }}>{FIELD_LABELS[field]}</span>
                      </span>
                    );
                  })}
                  {/* Line graph legends for Before */}
                  <span className="bill-summary-line-legend">
                    <Checkbox
                      checked={showBeforeDemand}
                      onChange={(e) => setShowBeforeDemand(e.target.checked)}
                      size="xs"
                      radius="sm"
                      color="gray"
                      className="bill-summary-legend-checkbox"
                      aria-label={t("result.billSummary.aria.beforeDemand")}
                    />
                    <svg width="28" height="14" style={{ marginRight: 2 }}>
                      <line x1="2" y1="7" x2="26" y2="7" stroke="#1b5e20" strokeWidth="2" />
                    </svg>
                    <span style={{ color: "#1b5e20", fontWeight: 600 }}>Demand</span>
                  </span>
                  <span className="bill-summary-line-legend">
                    <Checkbox
                      checked={showBeforeEnergy}
                      onChange={(e) => setShowBeforeEnergy(e.target.checked)}
                      size="xs"
                      radius="sm"
                      color="gray"
                      className="bill-summary-legend-checkbox"
                      aria-label={t("result.billSummary.aria.beforeEnergy")}
                    />
                    <svg width="28" height="14" style={{ marginRight: 2 }}>
                      <line x1="2" y1="7" x2="26" y2="7" stroke="#e65100" strokeWidth="2" />
                    </svg>
                    <span style={{ color: "#e65100", fontWeight: 600 }}>Energy</span>
                  </span>
                    </>
                  )}
                </div>
                {/* After row */}
                <div className="bill-summary-legend-row">
                  {hasAfter && (
                    <>
                  <span className="bill-summary-legend-label" style={{ color: colorScheme === "dark" ? "#e0e0e0" : "#222" }}>{t("result.billSummary.legend.after")}</span>
                  {FIELDS.map((field, i) => {
                    const legendKey = `after-${field}`;
                    if (!nonZeroFields.includes(field)) return null;
                    // Use AFTER_BAR_COLORS for after
                    const legendColor = AFTER_BAR_COLORS[i] || "#bbb";
                    return (
                      <span key={legendKey} className="bill-summary-legend-item">
                        <Checkbox
                          checked={visibleFields.includes(legendKey)}
                          onChange={(e) => {
                            setVisibleFields((v) =>
                              e.target.checked
                                ? [...v, legendKey]
                                : v.filter((f) => f !== legendKey)
                            );
                          }}
                          size="xs"
                          radius="sm"
                          color="teal"
                          className="bill-summary-legend-checkbox"
                          aria-label={FIELD_LABELS[field]}
                        />
                        <span className="bill-summary-legend-color" style={{ background: legendColor, position: "relative" }}>
                          <svg width="28" height="14" style={{ position: "absolute", left: 0, top: 0, pointerEvents: "none" }}>
                            <line x1="2" y1="12" x2="12" y2="2" stroke="#444" strokeWidth="1.5" />
                            <line x1="15" y1="12" x2="26" y2="2" stroke="#444" strokeWidth="1.5" />
                          </svg>
                        </span>
                        <span className="bill-summary-legend-label" style={{ color: colorScheme === "dark" ? "#e0e0e0" : "#222", fontWeight: 600 }}>{FIELD_LABELS[field]}</span>
                      </span>
                    );
                  })}
                  {/* Line graph legends for After */}
                  <span className="bill-summary-line-legend">
                    <Checkbox
                      checked={showAfterDemand}
                      onChange={(e) => setShowAfterDemand(e.target.checked)}
                      size="xs"
                      radius="sm"
                      color="teal"
                      className="bill-summary-legend-checkbox"
                      aria-label={t("result.billSummary.aria.afterDemand")}
                    />
                    <svg width="28" height="14" style={{ marginRight: 2 }}>
                      <line x1="2" y1="7" x2="26" y2="7" stroke="#81c784" strokeWidth="2" />
                    </svg>
                    <span style={{ color: "#81c784", fontWeight: 600 }}>Demand</span>
                  </span>
                  <span className="bill-summary-line-legend">
                    <Checkbox
                      checked={showAfterEnergy}
                      onChange={(e) => setShowAfterEnergy(e.target.checked)}
                      size="xs"
                      radius="sm"
                      color="teal"
                      className="bill-summary-legend-checkbox"
                      aria-label={t("result.billSummary.aria.afterEnergy")}
                    />
                    <svg width="28" height="14" style={{ marginRight: 2 }}>
                      <line x1="2" y1="7" x2="26" y2="7" stroke="#ffd54f" strokeWidth="2" />
                    </svg>
                    <span style={{ color: "#ffd54f", fontWeight: 600 }}>Energy</span>
                  </span>
                    </>
                  )}
                </div>
              </div>
              {/* Toolbar at right end of legend list */}
              <div className="bill-summary-toolbar" style={{ ...zoomBtnStyle, position: "static", alignSelf: "flex-start", marginLeft: 16 }}>
                <button type="button" className={`bill-summary-btn${colorScheme === "dark" ? " bill-summary-btn-dark" : ""}`}
                  aria-label="bill-summary-zoom-in"
                  title="Zoom In"
                  onClick={() => {
                    const totalMonths = Math.max(data.length, 1);
                    let [s, e] = zoomRange;
                    const rangeSpan = e - s;
                    // Allow zooming to 1/3 of a month for more detail
                    const minZoom = 1 / (totalMonths * 3);
                    if (rangeSpan <= minZoom) return;
                    const newSpan = clamp(rangeSpan / 2, minZoom, 1);
                    const center = s + rangeSpan / 2;
                    let ns = center - newSpan / 2;
                    let ne = center + newSpan / 2;
                    if (ns < 0) { ne -= ns; ns = 0; }
                    if (ne > 1) { const diff = ne - 1; ns -= diff; ne = 1; }
                    setZoomRange([ns, ne]);
                  }}
                >+
                </button>
                <button type="button" className={`bill-summary-btn${colorScheme === "dark" ? " bill-summary-btn-dark" : ""}`}
                  aria-label="bill-summary-zoom-out"
                  title="Zoom Out"
                  onClick={() => {
                    const totalMonths = Math.max(data.length, 1);
                    let [s, e] = zoomRange;
                    const rangeSpan = e - s;
                    const newSpan = clamp(rangeSpan * 2, 1 / totalMonths, 1);
                    const center = s + rangeSpan / 2;
                    let ns = center - newSpan / 2;
                    let ne = center + newSpan / 2;
                    if (ns < 0) { ne -= ns; ns = 0; }
                    if (ne > 1) { const diff = ne - 1; ns -= diff; ne = 1; }
                    setZoomRange([ns, ne]);
                  }}
                >-
                </button>
                <button type="button" className={`bill-summary-btn${colorScheme === "dark" ? " bill-summary-btn-dark" : ""}`}
                  aria-label="bill-summary-reset-zoom"
                  title="Reset Zoom"
                  onClick={() => setZoomRange([0, 1])}
                >Reset
                </button>
                {!inModalInstance && (
                  <button type="button" className={`bill-summary-btn${colorScheme === "dark" ? " bill-summary-btn-dark" : ""}`}
                    aria-label="bill-summary-open-fullscreen"
                    title="Fullscreen"
                    onClick={() => setFullscreenModal(true)}
                  >⛶
                  </button>
                )}
                <select
                  className={`bill-summary-btn${colorScheme === "dark" ? " bill-summary-btn-dark" : ""}`}
                  title="Download"
                  value=""
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === 'png') {
                      // Download PNG
                      const svgEl = svgRef.current;
                      if (!svgEl) return;

                      const copyComputedStyle = (sourceNode, targetNode) => {
                        const computed = window.getComputedStyle(sourceNode);
                        const propsToCopy = [
                          "fill",
                          "stroke",
                          "stroke-width",
                          "opacity",
                          "font-family",
                          "font-size",
                          "font-weight",
                          "text-anchor",
                        ];
                        propsToCopy.forEach((prop) => {
                          const val = computed.getPropertyValue(prop);
                          if (val) targetNode.style.setProperty(prop, val);
                        });
                      };

                      const inlineAllComputedStyles = (sourceSvg, targetSvg) => {
                        const sourceNodes = sourceSvg.querySelectorAll("*");
                        const targetNodes = targetSvg.querySelectorAll("*");
                        const n = Math.min(sourceNodes.length, targetNodes.length);
                        for (let i = 0; i < n; i++) {
                          copyComputedStyle(sourceNodes[i], targetNodes[i]);
                        }
                        copyComputedStyle(sourceSvg, targetSvg);
                      };

                      const serializer = new XMLSerializer();
                      const cloned = svgEl.cloneNode(true);

                      // Inline CSS-driven styles (especially axis ticks) so the SVG->PNG keeps them.
                      try {
                        inlineAllComputedStyles(svgEl, cloned);
                      } catch (err) {
                        // If computed-style inlining fails for any reason, still attempt export.
                        console.warn("Could not inline SVG styles for export:", err);
                      }

                      const bg = document.createElementNS("http://www.w3.org/2000/svg", "rect");
                      bg.setAttribute("x", "0");
                      bg.setAttribute("y", "0");
                      bg.setAttribute("width", svgEl.clientWidth || svgEl.getAttribute("width") || 1200);
                      bg.setAttribute("height", svgEl.clientHeight || svgEl.getAttribute("height") || 600);
                      bg.setAttribute("fill", colorScheme === "dark" ? "#111111" : "#ffffff");
                      cloned.insertBefore(bg, cloned.firstChild);
                      const svgString = serializer.serializeToString(cloned);
                      const img = new Image();
                      const canvas = document.createElement("canvas");
                      const w = svgEl.clientWidth || parseInt(svgEl.getAttribute("width"), 10) || 1200;
                      const h = svgEl.clientHeight || parseInt(svgEl.getAttribute("height"), 10) || 600;
                      canvas.width = w;
                      canvas.height = h;
                      const ctx = canvas.getContext("2d");
                      img.onload = () => {
                        ctx.drawImage(img, 0, 0);
                        canvas.toBlob((blob) => {
                          if (!blob) return;
                          const url = URL.createObjectURL(blob);
                          const a = document.createElement("a");
                          a.href = url;
                          const ts = new Date().toISOString().replace(/[:.]/g, "-");
                          a.download = `bill-summary-${ts}.png`;
                          document.body.appendChild(a);
                          a.click();
                          a.remove();
                          URL.revokeObjectURL(url);
                        });
                      };
                      img.src = "data:image/svg+xml;charset=utf-8," + encodeURIComponent(svgString);
                    } else if (value === 'csv') {
                      // Download CSV with Month and all bill fields for Before and After
                      try {
                        const rows = [];
                        // Build header: Month + available series fields + available demand/energy lines
                        const fieldLabels = FIELDS.map(f => FIELD_LABELS[f]);
                        const headerCols = [
                          t('result.billSummary.axis.month'),
                          ...(hasBefore ? fieldLabels.map(l => `${l} (${t('result.billSummary.before')})`) : []),
                          ...(hasAfter ? fieldLabels.map(l => `${l} (${t('result.billSummary.after')})`) : []),
                          ...(hasBefore ? [`${t('result.billSummary.demand')} (${t('result.billSummary.before')})`] : []),
                          ...(hasAfter ? [`${t('result.billSummary.demand')} (${t('result.billSummary.after')})`] : []),
                          ...(hasBefore ? [`${t('result.billSummary.energy')} (${t('result.billSummary.before')})`] : []),
                          ...(hasAfter ? [`${t('result.billSummary.energy')} (${t('result.billSummary.after')})`] : [])
                        ];
                        rows.push(headerCols.join(','));
                        // Build data rows - each row is a month
                        primarySeries.forEach((entry, idx) => {
                          const bm = safeBeforeMG[idx] || {};
                          const am = safeAfterMG[idx] || {};
                          const month = entry?.periodStart ? entry.periodStart.split("/")[1] : `Month ${idx + 1}`;
                          const beforeVals = hasBefore
                            ? FIELDS.map(f => bm[f] !== undefined ? bm[f] : '')
                            : [];
                          const afterVals = hasAfter
                            ? FIELDS.map(f => am[f] !== undefined ? am[f] : '')
                            : [];
                          const rowVals = [
                            month,
                            ...beforeVals,
                            ...afterVals,
                            ...(hasBefore ? [bm.demand !== undefined ? bm.demand : ''] : []),
                            ...(hasAfter ? [am.demand !== undefined ? am.demand : ''] : []),
                            ...(hasBefore ? [bm.energy !== undefined ? bm.energy : ''] : []),
                            ...(hasAfter ? [am.energy !== undefined ? am.energy : ''] : [])
                          ];
                          rows.push(rowVals.join(','));
                        });
                        const csvContent = rows.join('\n');
                        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        const ts = new Date().toISOString().replace(/[:.]/g, '-');
                        a.download = `bill-summary-${ts}.csv`;
                        a.href = url;
                        document.body.appendChild(a);
                        a.click();
                        a.remove();
                        URL.revokeObjectURL(url);
                      } catch (err) {
                        // Silent fail
                      }
                    }
                    // Reset dropdown
                    e.target.selectedIndex = 0;
                  }}
                >
                  <option value="" disabled hidden>{t('common.download')}</option>
                  <option value="png">PNG</option>
                  <option value="csv">CSV</option>
                </select>
              </div>
            </div>
            {/* Chart area: 100% */}
            <div className="bill-summary-chart-area indicator-downloadable" style={{ backgroundColor: colorScheme === "dark" ? "#111111" : "#fff" }}>
              <div ref={ref} style={{ flex: 1 }}></div>
              {tooltip.visible && (() => {
                let beforeOrAfter = "", fieldKey = "";
                if (tooltip.field) {
                  const parts = tooltip.field.split(" ");
                  beforeOrAfter = parts[0];
                  fieldKey = parts.slice(1).join(" ");
                }
                const colorIdx = FIELDS.indexOf(fieldKey);
                let color;
                if (beforeOrAfter === "before") {
                  color = "#228be6";
                } else if (beforeOrAfter === "after") {
                  color = AFTER_BAR_COLORS[colorIdx] || "#bbb";
                } else {
                  color = COLORS[colorIdx] || "#888";
                }
                const label = FIELD_LABELS[fieldKey] || fieldKey;
                return (
                  <div className={`bill-summary-tooltip${colorScheme === "dark" ? " bill-summary-tooltip-dark" : ""}`}
                    style={{ left: tooltip.x + 24, top: tooltip.y - 8 }}>
                    <div style={{ fontWeight: 700, marginBottom: 4, display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{ width: 18, height: 18, background: color, borderRadius: 3, display: "inline-block", border: "1px solid #ccc", marginRight: 4 }}></span>
                      <span>{label} - {beforeOrAfter.charAt(0).toUpperCase() + beforeOrAfter.slice(1)}</span>
                    </div>
                    <div style={{ fontWeight: 600, fontSize: 16 }}>{formatDisplayNumber(tooltip.value)}</div>
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      </div>
      <FullscreenModal
        open={fullscreenModal}
        onClose={() => setFullscreenModal(false)}
        title={"Bill Summary Chart"}
      >
        <div className={`bill-summary-modal-chart${colorScheme === "dark" ? " bill-summary-modal-chart-dark" : ""}`}>
          <div className="bill-summary-modal-inner">
            <BillSummaryChart
              key="modal"
              beforeMG={beforeMG}
              afterMG={afterMG}
              inModalInstance={true}
            />
          </div>
        </div>
      </FullscreenModal>
    </>
  );
};
export default BillSummaryChart;
